import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiEye, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteLeader, getAdminLeaders } from 'apis/adminLeaders';
import { getAdminLeaderCategories } from 'apis/adminLeaderCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const LeadersList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('leader-categories', { pageSize: 1000, languageCode: languageCodeFilter || 'vi' }, getAdminLeaderCategories);
    const categories = categoriesData.items;
    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        categoryId: categoryFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, categoryFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('leaders', queryParams, getAdminLeaders);
    const deleteMutation = useAdminDeleteMutation('leaders', deleteLeader);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter, categoryFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) { toast.success('Xóa thành công'); } else toast.error(res.message);
        } catch { toast.error('Xóa thất bại'); }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        { header: 'Ảnh', render: (row) => row.image ? <img src={row.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }} /> : <div style={{ width: 40, height: 40, background: '#e5e7eb', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>{row.name?.[0] || '?'}</div> },
        { field: 'name', header: 'Tên' },
        { header: 'Danh mục', render: (row) => {
            const cat = categories.find(c => c.id === row.categoryId);
            const activeLangId = languages.find(l => l.code === (languageCodeFilter || 'vi'))?.id;
            const t = cat?.translations?.find(tr => tr.languageId === activeLangId) || cat?.translations?.[0];
            return t?.name || t?.title || cat?.name || `ID: ${row.categoryId}`;
        } },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { header: 'Thao tác', render: (row) => <div className="action-buttons"><button className="icon-btn" onClick={() => navigate(`/admin/leaders/view/${row.id}`)} title="Xem chi tiết"><FiEye size={15} /></button><button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/leaders/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button><button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button></div> },
    ];

    return (
        <div className="admin-page">
            <PageHeader title="Ban Lãnh đạo" subtitle="Quản lý thành viên ban lãnh đạo" action={<Button onClick={() => navigate('/admin/leaders/create')}>+ Thêm Lãnh đạo</Button>} />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm theo tên..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item"><Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} /></div>

                <div className="filter-item">
                    <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả danh mục' },
                            ...categories.filter(c => c.isActive === 1 || c.isActive === true).map(c => {
                                const activeLangId = languages.find(l => l.code === (languageCodeFilter || 'vi'))?.id;
                                const t = c?.translations?.find(tr => tr.languageId === activeLangId) || c?.translations?.[0];
                                return { value: c.id, label: t?.name || t?.title || c?.name || `ID: ${c.id}` };
                            })
                        ]} />
                </div>

                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]} />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Xóa Lãnh đạo" message="Bạn có chắc chắn muốn xóa không?" />
        </div>
    );
};

export default LeadersList;
